import React, { useState, useEffect } from 'react';
import { ensureSessionHeaders } from '../../lib/serverSession';

interface LockedWallet {
  id: string;
  user_id: number;
  wallet_address: string;
  network: string;
  wallet_type?: string; // v4.3.59: 'centralized' or 'decentralized'
  registered_at: string;
  registered_by: string;
  is_active: boolean;
}

interface LockedWalletManagerProps {
  userId: number;
  onWalletsLoaded?: (wallets: LockedWallet[]) => void;
}

// Map from UI network names to DB locked wallet network names
// SECURITY (v4.0.53): Only BSC is supported for withdrawals going forward
export const NETWORK_TO_LOCKED_KEY: Record<string, string> = {
  'USDT-BEP20': 'BSC',
  'BSC': 'BSC',
};

// Legacy mapping kept for display purposes only (existing wallets on other networks)
const LEGACY_NETWORKS = ['TRC20', 'Ethereum', 'Polygon', 'TRON'];

const EVM_REGEX = /^0x[a-fA-F0-9]{40}$/;

// v4.7.20: Feature flag — the dedicated Decentralized (NFT) Wallet registration section is hidden.
// The withdrawal RPC continues to accept BOTH centralized and decentralized active wallets (v4.7.18),
// so any pre-existing decentralized wallet still functions for withdrawals; only the registration UI
// for new decentralized wallets is hidden until the operator opens it in a future release.
const DECENTRALIZED_WALLET_SECTION_ENABLED = false;

export const LockedWalletManager: React.FC<LockedWalletManagerProps> = ({ userId, onWalletsLoaded }) => {
  const [wallets, setWallets] = useState<LockedWallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [showRegisterForm, setShowRegisterForm] = useState<'centralized' | 'decentralized' | null>(null);
  const [newNetwork] = useState('BSC');
  const [newAddress, setNewAddress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmStep, setConfirmStep] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [personalWalletConfirmed, setPersonalWalletConfirmed] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationWarning, setVerificationWarning] = useState<string | null>(null);
  // Phase 5 (A): email OTP step-up state for wallet registration
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);

  useEffect(() => { loadWallets(); }, [userId]);

  const loadWallets = async () => {
    try {
      setLoading(true);
      const headers = await ensureSessionHeaders();
      const res = await fetch('/api/user/locked-wallets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ operation: 'get_locked_wallets' })
      });
      const data = await res.json();
      if (data.success) {
        setWallets(data.wallets || []);
        onWalletsLoaded?.(data.wallets || []);
      }
    } catch (err) {
      console.error('Error loading locked wallets:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateAddress = (address: string): boolean => {
    return EVM_REGEX.test(address.trim());
  };

  // Phase 5 (A): request an email OTP for the wallet-registration step-up.
  // Email is resolved server-side from the session — never sent by the client.
  const handleSendOtp = async () => {
    setError(null);
    setSendingOtp(true);
    try {
      const headers = await ensureSessionHeaders();
      const res = await fetch('/api/user/locked-wallets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ operation: 'request_wallet_otp' })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to send verification code.');
      setOtpSent(true);
      setSuccess('📧 Verification code sent to your email. Enter it below to finish.');
      setTimeout(() => setSuccess(null), 6000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSendingOtp(false);
    }
  };

  const handleRegister = async () => {
    setError(null);
    const trimmed = newAddress.trim();
    if (!trimmed) { setError('Please enter a wallet address.'); return; }
    if (!validateAddress(trimmed)) {
      setError('Invalid BSC address. Must start with 0x followed by 40 hex characters.');
      return;
    }
    if (!acknowledged) { setError('Please acknowledge the permanence warning before saving.'); return; }
    if (showRegisterForm === 'decentralized' && !personalWalletConfirmed) {
      setError('Please confirm that this is a personal wallet (not an exchange wallet).');
      return;
    }
    if (!/^\d{6}$/.test(otpCode.trim())) {
      setError('Enter the 6-digit verification code sent to your email.');
      return;
    }

    setRegistering(true);
    try {
      const headers = await ensureSessionHeaders();
      const res = await fetch('/api/user/locked-wallets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          operation: 'register_wallet',
          data: {
            wallet_address: trimmed,
            network: newNetwork,
            wallet_type: showRegisterForm,
            personal_wallet_confirmed: showRegisterForm === 'decentralized' ? true : false,
            verification_code: otpCode.trim()
          }
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to register wallet');
      setSuccess(`✅ ${showRegisterForm === 'decentralized' ? 'Decentralized' : 'Withdrawal'} wallet registered successfully!`);
      setShowRegisterForm(null); setConfirmStep(false); setAcknowledged(false); setPersonalWalletConfirmed(false);
      setNewAddress(''); setOtpSent(false); setOtpCode('');
      await loadWallets();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setRegistering(false);
    }
  };

  // Categorize wallets
  const centralizedWallets = wallets.filter(w => w.wallet_type === 'centralized');
  const decentralizedWallets = wallets.filter(w => w.wallet_type === 'decentralized');
  const uncategorizedWallets = wallets.filter(w => !w.wallet_type && !LEGACY_NETWORKS.includes(w.network));
  const legacyWallets = wallets.filter(w => LEGACY_NETWORKS.includes(w.network));

  const hasCentralized = centralizedWallets.length > 0 || uncategorizedWallets.length > 0;
  const hasDecentralized = decentralizedWallets.length > 0;

  if (loading) return <div className="text-gray-400 text-sm py-2">Loading wallet settings...</div>;

  return (
    <div className="space-y-4 mb-4">
      {success && <div className="bg-green-900/30 border border-green-500/30 rounded p-2 text-green-300 text-sm">{success}</div>}
      {error && <div className="bg-red-900/30 border border-red-500/30 rounded p-2 text-red-300 text-sm">{error}</div>}

      {/* ═══════════ SECTION 1: Withdrawal Wallet (USDT — accepts any BSC wallet, exchange OR self-custody) ═══════════ */}
      <div className="bg-gray-800/50 border border-blue-700/50 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            💼 Withdrawal Wallet <span className="text-blue-400 text-xs font-normal">(USDT Withdrawals)</span>
          </h3>
          {!hasCentralized && !hasDecentralized && showRegisterForm !== 'centralized' && (
            <button onClick={() => { setShowRegisterForm('centralized'); setError(null); }}
              className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded transition-colors">
              + Register Withdrawal Wallet
            </button>
          )}
        </div>

        <div className="bg-blue-900/20 border border-blue-500/30 rounded p-3 mb-3">
          <p className="text-blue-200 text-xs leading-relaxed">
            <strong>What is a Withdrawal Wallet?</strong> This is the BSC (BNB Smart Chain) wallet address where your
            <strong> USDT commission withdrawals</strong> will be sent. It can be an exchange account
            (<strong>Binance, Coinbase, Bybit, Luno</strong>) OR a self-custody wallet
            (<strong>MetaMask, Trust Wallet, SafePal</strong>) — both are accepted.
          </p>
          <p className="text-blue-300 text-xs mt-2">
            💡 Once registered, this address is permanently locked to your account and cannot be changed by you.
            Please make sure the address is correct before saving.
          </p>
        </div>

        {/* Display all active BSC wallets — centralized, decentralized, and legacy untyped — under the unified Withdrawal Wallet view */}
        {[...centralizedWallets, ...uncategorizedWallets, ...decentralizedWallets].map(w => (
          <div key={w.id} className="bg-gray-700/50 rounded p-3 flex items-center justify-between mb-2">
            <div>
              <span className="text-blue-400 font-medium text-sm">BSC (BNB Smart Chain)</span>
              <p className="text-gray-300 font-mono text-xs mt-1 break-all">{w.wallet_address}</p>
              <p className="text-gray-500 text-xs mt-1">Registered {new Date(w.registered_at).toLocaleDateString()} by {w.registered_by}</p>
            </div>
            <span className="text-green-400 text-xs">🔒 Locked</span>
          </div>
        ))}
        {!hasCentralized && !hasDecentralized && showRegisterForm !== 'centralized' && (
          <p className="text-red-300 text-sm">⚠️ No withdrawal wallet registered. You must register one to enable USDT withdrawals.</p>
        )}
      </div>

      {/* ═══════════ SECTION 2: Decentralized Wallets (NFT — required; also valid for withdrawals) ═══════════ */}
      {/* v4.7.20: Section hidden behind DECENTRALIZED_WALLET_SECTION_ENABLED flag — operator will re-open in a future release. */}
      {DECENTRALIZED_WALLET_SECTION_ENABLED && (
      <div className="bg-gray-800/50 border border-purple-700/50 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            🔐 Decentralized Wallet <span className="text-purple-400 text-xs font-normal">(NFT Share Certificates — required)</span>
          </h3>
          {!hasDecentralized && showRegisterForm !== 'decentralized' && (
            <button onClick={() => { setShowRegisterForm('decentralized'); setError(null); }}
              className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded transition-colors">
              + Register Decentralized Wallet
            </button>
          )}
        </div>

        <div className="bg-purple-900/20 border border-purple-500/30 rounded p-3 mb-3">
          <p className="text-purple-200 text-xs leading-relaxed">
            <strong>What is a Decentralized Wallet?</strong> A decentralized (self-custody) wallet like
            <strong> SafePal, Exodus, MetaMask, or Trust Wallet</strong> is one where <strong>you control the private keys</strong>.
            This type of wallet is <strong>required</strong> for receiving <strong>NFT Share Certificates</strong> because NFTs can only
            be sent to wallets that support smart contract interactions.
          </p>
          <p className="text-purple-300 text-xs mt-2">
            🎨 Your Aureus NFT Share Certificates will be delivered to this wallet.
          </p>
          <p className="text-purple-300 text-xs mt-2">
            💵 A Decentralized wallet may also be used to receive <strong>USDT withdrawals</strong>. Centralized exchange wallets,
            however, cannot receive NFTs — so if you only register one wallet, a Decentralized wallet covers both.
          </p>
        </div>

        {/* Display decentralized wallets */}
        {decentralizedWallets.map(w => (
          <div key={w.id} className="bg-gray-700/50 rounded p-3 flex items-center justify-between mb-2">
            <div>
              <span className="text-purple-400 font-medium text-sm">BSC (BNB Smart Chain) — Decentralized</span>
              <p className="text-gray-300 font-mono text-xs mt-1 break-all">{w.wallet_address}</p>
              <p className="text-gray-500 text-xs mt-1">Registered {new Date(w.registered_at).toLocaleDateString()} by {w.registered_by}</p>
            </div>
            <span className="text-green-400 text-xs">🔒 Locked</span>
          </div>
        ))}
        {!hasDecentralized && showRegisterForm !== 'decentralized' && (
          <p className="text-yellow-300 text-sm text-opacity-80">ℹ️ No decentralized wallet registered yet. Register one to receive your NFT Share Certificates (it can also receive USDT withdrawals).</p>
        )}
      </div>
      )}

      {/* Legacy wallets */}
      {legacyWallets.length > 0 && (
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 opacity-60">
          <p className="text-gray-500 text-xs font-medium uppercase tracking-wide mb-2">Legacy Wallets (No longer supported for withdrawals)</p>
          {legacyWallets.map(w => (
            <div key={w.id} className="bg-gray-800/50 rounded p-3 flex items-center justify-between border border-gray-700/50 mb-2">
              <div>
                <span className="text-gray-500 font-medium text-sm">{w.network}</span>
                <span className="text-gray-600 text-xs ml-2">— Legacy</span>
                <p className="text-gray-500 font-mono text-xs mt-1 break-all">{w.wallet_address}</p>
              </div>
              <span className="text-gray-600 text-xs">⚠️ Legacy</span>
            </div>
          ))}
        </div>
      )}

      {/* ═══════════ REGISTER WALLET FORM (shared for both types) ═══════════ */}
      {showRegisterForm && !confirmStep && (
        <div className={`border rounded-xl p-4 mt-2 ${showRegisterForm === 'centralized' ? 'bg-blue-900/10 border-blue-600/40' : 'bg-purple-900/10 border-purple-600/40'}`}>
          <h4 className="text-white font-medium text-sm mb-3">
            Register {showRegisterForm === 'centralized' ? '💼 Withdrawal' : '🔐 Decentralized'} BSC Wallet
          </h4>

          {showRegisterForm === 'centralized' ? (
            <div className="bg-blue-900/30 border border-blue-500/40 rounded p-3 mb-3 space-y-2">
              <p className="text-blue-300 text-xs">ℹ️ Register the <strong>BSC wallet address</strong> where your <strong>USDT commission withdrawals</strong> will be sent. This can be an exchange account (Binance, Coinbase, Bybit, Luno) OR a self-custody wallet (MetaMask, Trust Wallet, SafePal).</p>
              <p className="text-blue-200 text-xs">Once registered, this address is permanently locked to your account and cannot be changed by you. Please double-check the address before saving.</p>
            </div>
          ) : (
            <div className="bg-purple-900/30 border border-purple-500/40 rounded p-3 mb-3 space-y-2">
              <p className="text-purple-300 text-xs">ℹ️ Register your <strong>self-custody wallet</strong> (e.g., SafePal, MetaMask, Trust Wallet) for receiving <strong>NFT Share Certificates</strong> and (optionally) <strong>USDT withdrawals</strong>.</p>
              <p className="text-red-300 text-xs font-semibold">⚠️ Do NOT use an exchange wallet here. NFTs require a wallet where you control the private keys.</p>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="block text-gray-300 text-xs mb-1">Network</label>
              <div className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm">BSC (BNB Smart Chain)</div>
            </div>
            <div>
              <label className="block text-gray-300 text-xs mb-1">Wallet Address (BEP-20)</label>
              <input type="text" value={newAddress} onChange={(e) => setNewAddress(e.target.value)}
                placeholder="0x..." className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm font-mono" />
            </div>
            <div className="flex gap-2">
              <button onClick={async () => {
                setError(null); setVerificationWarning(null);
                const trimmed = newAddress.trim();
                if (!trimmed) { setError('Enter a wallet address.'); return; }
                if (!validateAddress(trimmed)) { setError('Invalid BSC address. Must start with 0x followed by 40 hex characters.'); return; }
                setVerifying(true);
                try {
                  const headers = await ensureSessionHeaders();
                  const verifyRes = await fetch('/api/user/verify-wallet', {
                    method: 'POST', headers: { 'Content-Type': 'application/json', ...headers },
                    body: JSON.stringify({ wallet_address: trimmed, network: 'BSC' })
                  });
                  if (verifyRes.status === 429) { setError('Verification service is temporarily busy. Try again in a few seconds.'); return; }
                  const verifyData = await verifyRes.json();
                  if (!verifyData.success) { setError(verifyData.error || 'Wallet verification failed.'); return; }
                  if (!verifyData.valid) { setError('This wallet address could not be verified on the BSC blockchain.'); return; }
                  if (verifyData.valid && !verifyData.hasActivity) {
                    setVerificationWarning(verifyData.verificationSkipped
                      ? '⚠️ On-chain verification unavailable. Address format is valid but could not be confirmed on-chain.'
                      : '⚠️ This wallet has no transaction history on BSC. Please ensure this is the correct address.');
                  }
                  setConfirmStep(true);
                } catch (verifyErr: any) {
                  setError('Could not verify wallet address.'); console.error('[LockedWalletManager] Verification error:', verifyErr);
                } finally { setVerifying(false); }
              }} disabled={verifying}
                className={`${showRegisterForm === 'centralized' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700'} disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm px-4 py-2 rounded transition-colors`}>
                {verifying ? '🔄 Verifying on BSC...' : 'Continue'}
              </button>
              <button onClick={() => { setShowRegisterForm(null); setError(null); setVerificationWarning(null); setNewAddress(''); setOtpSent(false); setOtpCode(''); }}
                disabled={verifying} className="bg-gray-600 hover:bg-gray-500 disabled:cursor-not-allowed text-white text-sm px-4 py-2 rounded transition-colors">
                Cancel
              </button>
            </div>
            {verifying && (
              <div className="mt-3 bg-blue-900/30 border border-blue-500/30 rounded p-3">
                <p className="text-blue-300 text-xs animate-pulse">🔍 Verifying wallet address on BSC blockchain...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation step */}
      {showRegisterForm && confirmStep && (
        <div className="bg-red-900/20 border border-red-500/40 rounded-xl p-4 mt-2">
          <h4 className="text-red-300 font-medium text-sm mb-3">⚠️ Confirm Permanent Wallet Registration</h4>
          {verificationWarning ? (
            <div className="bg-yellow-900/30 border border-yellow-500/40 rounded p-3 mb-3">
              <p className="text-yellow-300 text-xs">{verificationWarning}</p>
            </div>
          ) : (
            <div className="bg-green-900/30 border border-green-500/40 rounded p-3 mb-3">
              <p className="text-green-300 text-xs">✅ Wallet address verified on BSC blockchain.</p>
            </div>
          )}
          <div className="bg-gray-700/50 rounded p-3 mb-3">
            <p className="text-gray-300 text-xs"><strong>Purpose:</strong> {showRegisterForm === 'centralized' ? '💼 Withdrawal Wallet (USDT)' : '🔐 Decentralized Wallet (NFT Share Certificates)'}</p>
            <p className="text-gray-300 text-xs"><strong>Network:</strong> BSC (BNB Smart Chain)</p>
            <p className="text-gray-300 text-xs font-mono mt-1"><strong>Address:</strong> {newAddress.trim()}</p>
          </div>
          <label className="flex items-start gap-2 mb-3 cursor-pointer">
            <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} className="mt-1 accent-yellow-500" />
            <span className="text-yellow-200 text-xs">I understand this wallet address will be permanently locked to my account and CANNOT be changed by me.</span>
          </label>
          {showRegisterForm === 'decentralized' && (
            <label className="flex items-start gap-2 mb-3 cursor-pointer">
              <input type="checkbox" checked={personalWalletConfirmed} onChange={(e) => setPersonalWalletConfirmed(e.target.checked)} className="mt-1 accent-yellow-500" />
              <span className="text-yellow-200 text-xs">
                I confirm this is a <strong>personal self-custody wallet</strong> where I control the private keys.
                This is <strong>NOT</strong> an exchange wallet. I accept full responsibility for providing the correct wallet address.
              </span>
            </label>
          )}
          {/* Phase 5 (A): email OTP step-up — required before the wallet is locked */}
          <div className="bg-gray-800/60 border border-yellow-600/40 rounded p-3 mb-3">
            {!otpSent ? (
              <>
                <p className="text-yellow-200 text-xs mb-2">🔐 For your security, we'll email you a 6-digit code to confirm this wallet before it is locked to your account.</p>
                <button onClick={handleSendOtp} disabled={sendingOtp}
                  className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm px-4 py-2 rounded transition-colors">
                  {sendingOtp ? 'Sending…' : '📧 Send verification code'}
                </button>
              </>
            ) : (
              <>
                <label className="block text-yellow-200 text-xs mb-1">Enter the 6-digit code sent to your email</label>
                <input type="text" inputMode="numeric" maxLength={6} value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="123456"
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm font-mono tracking-widest" />
                <button onClick={handleSendOtp} disabled={sendingOtp}
                  className="mt-2 text-yellow-300 hover:text-yellow-200 text-xs underline disabled:text-gray-500">
                  {sendingOtp ? 'Sending…' : 'Resend code'}
                </button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={handleRegister} disabled={!acknowledged || (showRegisterForm === 'decentralized' && !personalWalletConfirmed) || !otpSent || !/^\d{6}$/.test(otpCode.trim()) || registering}
              className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm px-4 py-2 rounded transition-colors">
              {registering ? 'Saving...' : '🔒 Lock This Wallet Permanently'}
            </button>
            <button onClick={() => { setConfirmStep(false); setAcknowledged(false); setPersonalWalletConfirmed(false); setVerificationWarning(null); setOtpSent(false); setOtpCode(''); }}
              className="bg-gray-600 hover:bg-gray-500 text-white text-sm px-4 py-2 rounded transition-colors">
              Go Back
            </button>
          </div>
          {/* v4.7.30: Show registration errors inline next to the action buttons so the user
              gets immediate feedback if the API rejects the registration (previously the
              error was only rendered at the top of the manager, far above the button). */}
          {error && (
            <div className="mt-3 bg-red-900/40 border border-red-500/50 rounded p-2 text-red-300 text-xs">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LockedWalletManager;
export type { LockedWallet };
